export interface SaleDetails {
  name: string;
  email: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  saleDate: string;
  size?: string;
  varietyPackSelections?: string[];
}

/**
 * A size a print can be bought in. `label` is what the customer picks and what
 * Jessie reads on the order (e.g. `11x14"`); `price` is in whole dollars, to
 * match `Art.price`.
 */
export interface PrintSize {
  label: string;
  price: number;
}

/**
 * The size/price ladder taken from Jessie's existing listings. Used to seed new
 * prints; any print can override its own prices in the admin panel, since
 * `sizes` is stored per-print.
 */
export const DEFAULT_PRINT_SIZES: readonly PrintSize[] = Object.freeze([
  { label: '8x10"', price: 35 },
  { label: '11x14"', price: 50 },
  { label: '16x20"', price: 60 },
]);

/** Square art only fits square paper. */
export const SQUARE_PRINT_SIZES: readonly PrintSize[] = Object.freeze([
  { label: '12x12"', price: 45 },
]);

export interface Art {
  id: string;
  name: string;
  description: string;
  createdDate: string;
  storageLocation: string;
  price?: number;
  /**
   * Sizes this print is sold in. Absent or empty means the piece has a single
   * price (`price`) and no size picker — which is how cards and every
   * pre-2026 reproduction behave.
   */
  sizes?: PrintSize[];
  soldDate?: string;
  saleDetails?: SaleDetails;
  reproductionSaleDetails? : SaleDetails[];
  reproductionType?: 'card' | 'print'
  shipped?: boolean;
  isReproduction?: boolean;
  isActive?: boolean;
  url?: string;
  /**
   * Pixel size of the display copy (see `displayImageFor`). Recorded at upload
   * so a gallery knows the shape of every painting on it before fetching a
   * single byte, and can lay out the whole wall on its first frame.
   */
  displayWidth?: number;
  displayHeight?: number;
}

/**
 * Where a painting's picture lives, and what shape it is.
 *
 * Originals are Jessie's masters: several thousand pixels on a side, up to 10MB
 * each. The home wall shows them at about 370px wide, so serving those files to
 * a gallery meant a visitor downloading around a hundred megabytes to look at
 * postage stamps. `scripts/build-display-images.mjs` writes a resized copy of
 * every painting alongside it, and this points at that copy.
 *
 * The URL is computed rather than fetched. Storage will serve an object to
 * anyone who asks (see storage.rules), so no per-painting round trip is needed
 * to find out where its picture is — which is what lets a wall of forty-four
 * paintings lay itself out immediately.
 *
 * Returns null for a piece with no display copy yet, so callers can fall back
 * to the original rather than render a broken image.
 */
export function displayImageFor(
  art: Pick<Art, "storageLocation" | "displayWidth" | "displayHeight">
): { url: string; ratio: number } | null {
  const { storageLocation, displayWidth, displayHeight } = art;
  if (!storageLocation || !displayWidth || !displayHeight) return null;
  return {
    url: storageUrlFor(displayPathFor(storageLocation)),
    ratio: displayWidth / displayHeight,
  };
}

/** Where the display copy of an original is kept. */
export function displayPathFor(storageLocation: string): string {
  return `display/${storageLocation}.webp`;
}

/** A public, tokenless URL for an object in the project's Storage bucket. */
export function storageUrlFor(path: string): string {
  const bucket = "art-webapp-812f7.appspot.com";
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(
    path
  )}?alt=media`;
}

/** The sizes on offer, or `[]` when the piece is sold at a single price. */
export function sizesFor(art: Pick<Art, "sizes"> | null | undefined): PrintSize[] {
  return art?.sizes?.length ? art.sizes : [];
}

/**
 * Whether an original has sold.
 *
 * There is no reliable structured flag for this: no piece in `home-images` has
 * ever had `soldDate` set. Jessie records a sale by typing "SOLD" into the
 * `price` field, into the start of the description, or both — so all three
 * signals are checked.
 */
export function isSold(
  art: Pick<Art, "soldDate" | "price" | "description"> | null | undefined
): boolean {
  if (!art) return false;
  if (art.soldDate) return true;
  if (/^\s*sold/i.test(String(art.price ?? ""))) return true;
  return /^\s*sold/i.test(art.description ?? "");
}

/** Lowest price across a piece's sizes, falling back to its flat price. */
export function lowestPrice(
  art: Pick<Art, "sizes" | "price"> | null | undefined
): number | undefined {
  const sizes = sizesFor(art);
  if (!sizes.length) return art?.price;
  return sizes.reduce((min, size) => (size.price < min ? size.price : min), sizes[0].price);
}

/**
 * Newest work first, oldest last, and the same answer every time.
 *
 * The galleries used to shuffle on every load. A justified wall is composed —
 * neighbours are chosen so their shapes sit well together — and none of that
 * survives a random order. A stable order also means a visitor who comes back
 * to find the piece they liked finds it where they left it.
 *
 * `createdDate` is written by the admin panel as `new Date().toLocaleString()`,
 * so it is parsed rather than compared as text, and pieces uploaded before the
 * field existed sort to the end. `id` breaks ties so the order is total.
 */
export function byNewestFirst(a: Art, b: Art): number {
  const timeOf = (art: Art) => {
    const parsed = Date.parse(art.createdDate ?? "");
    return Number.isNaN(parsed) ? 0 : parsed;
  };
  const difference = timeOf(b) - timeOf(a);
  if (difference) return difference;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
